import { useRef, useState, useCallback, useEffect } from "react";
import { getAccessToken, tryRestoreSession } from "@/lib/api";

const WS_BASE = import.meta.env.VITE_WS_URL ?? "";

export interface DebugSummary {
  type: "debug_summary";
  duration_ms: number;
  lane?: string;
  classifier_skipped?: boolean;
  fast_path_type?: string;
  governor?: {
    lane?: string;
    model?: string;
    hot_limit?: number;
    sections?: string[];
    cold_retrieval?: boolean;
    force_phased?: boolean;
    complexity?: {
      score?: number;
      model?: string;
      phased?: boolean;
      signals?: Record<string, unknown>;
      reasoning?: string;
    };
  };
  classifier?: {
    duration_ms?: number;
    input_tokens?: number;
    output_tokens?: number;
    task_type?: string;
    model?: string;
    tool_groups?: string[];
    complexity?: string;
  };
  agent_loop?: {
    model?: string;
    input_tokens?: number;
    output_tokens?: number;
    tool_calls?: number;
    iterations?: number;
    files_changed?: Array<{ path: string; action: string }>;
  };
  totals?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    tool_calls?: number;
    api_calls?: number;
  };
}

export type ServerMessage =
  | { type: "text_delta"; content: string }
  | { type: "text_done"; content: string }
  | { type: "response_start" }
  | { type: "response_end" }
  | { type: "widget"; widget_type: string; data: Record<string, unknown>; actions?: Array<{ id: string; label: string; style?: string }> }
  | { type: "activity"; description: string }
  | { type: "shell_output"; data: string }
  | { type: "error"; message: string }
  | { type: "agent_status"; connected: boolean }
  | { type: "session_updated"; session_id: string; title: string }
  | { type: "session_rotated"; new_session_id: string }
  | { type: "greeting"; content: string }
  | { type: "route_to_project"; project_id: string; project_name: string; forward_prompt?: string }
  | { type: "context_usage"; percent: number; tokens_used: number; tokens_total: number; compressed?: boolean }
  | DebugSummary;

interface UseWebSocketOptions {
  projectId: string;
  onMessage?: (msg: ServerMessage) => void;
}

type ConnectionState = "connecting" | "connected" | "disconnected";

export function useWebSocket({ projectId, onMessage }: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ConnectionState>("disconnected");
  const [agentConnected, setAgentConnected] = useState(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token || !projectId) return;

    const base = WS_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, "ws");
    // No token in URL — sent via first message after connect
    const url = `${wsUrl}/ws/client`;

    setState("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send auth as first message (keeps JWT out of URL/logs)
      ws.send(JSON.stringify({
        type: "auth",
        token,
        project_id: projectId,
      }));
      setState("connected");
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        if (msg.type === "agent_status") {
          setAgentConnected(msg.connected);
        }
        onMessageRef.current?.(msg);
      } catch (_err) {
        console.error("[ws] Failed to parse message:", event.data);
      }
    };

    ws.onclose = (event) => {
      setState("disconnected");
      wsRef.current = null;

      // 4003 = auth expired — refresh token, then reconnect
      if (event.code === 4003 || event.code === 403) {
        tryRestoreSession().then((restored) => {
          if (restored) {
            reconnectTimer.current = setTimeout(connect, 500);
          }
          // If refresh failed, don't reconnect — user needs to re-login
        });
        return;
      }

      // 4001 = auth rejected — try refresh once (token may have expired
      // between page load and WebSocket connect), then give up
      if (event.code === 4001) {
        tryRestoreSession().then((restored) => {
          if (restored) {
            reconnectTimer.current = setTimeout(connect, 500);
          }
          // If refresh also failed, permanently rejected — user must re-login
        });
        return;
      }

      // Other close codes — reconnect after delay
      reconnectTimer.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => {
      // onclose will fire after this
    };
  }, [projectId]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setState("disconnected");
  }, []);

  const send = useCallback((msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const sendChat = useCallback((content: string, sessionId: string) => {
    send({ type: "chat", content, session_id: sessionId });
  }, [send]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    state,
    agentConnected,
    connect,
    disconnect,
    send,
    sendChat,
  };
}
